import { type PostContent } from '@backend/lib/platforms/tiktok'
import { AiLabeledImage } from './ai-label-preview-overlay'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'

type Props = {
  postContent: PostContent
  account: { id: string; displayName: string; imageUrl?: string; subline?: string } | undefined
  _onStartAuth: () => void
  /** Draw the visible AI label that will be baked into photo media at publish.
   *  TikTok photo posts need it: `is_aigc` is a video-only field, so a photo
   *  post has no native flag to fall back on. */
  showAiLabel?: boolean
  /** Appearance of that label, so the preview matches the user's settings. */
  aiLabelStyle?: AiLabelStyle
}

export function WorkspacePreviewTiktok({
  account,
  postContent,
  _onStartAuth,
  showAiLabel = false,
  aiLabelStyle,
}: Props) {
  return (
    <div className="mx-auto flex h-full w-full flex-col items-center justify-center p-3">
      {/* TikTok-style vertical video container */}
      <div className="aspect-9/16 relative flex max-h-[600px] w-full max-w-[340px] flex-col overflow-hidden rounded-lg bg-black">
        {/* Video */}
        <div className="relative flex h-full w-full items-center justify-center">
          {postContent?.type === 'photo' ? (
            postContent.photoImages.length > 0 ? (
              // Sized to the picture rather than the 9:16 frame. With
              // `h-full w-full object-contain` the element box is the whole
              // frame, so the label anchored to its bottom-left landed on the
              // black letterbox instead of the image. Centring is done by the
              // flex parent, which keeps the framing identical.
              <AiLabeledImage
                src={
                  postContent.photoImages[postContent.photoCoverIndex] ?? postContent.photoImages[0]
                }
                className="max-h-full max-w-full object-contain"
                crossOrigin="anonymous"
                showAiLabel={showAiLabel}
                aiLabelStyle={aiLabelStyle}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-900">
                <p className="text-gray-400">No photos</p>
              </div>
            )
          ) : postContent?.url ? (
            <video
              src={postContent.url}
              className="h-full w-full object-contain"
              controls
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-900">
              <p className="text-gray-400">No video</p>
            </div>
          )}
        </div>

        {/* Account info overlay (top) */}
        <div className="absolute left-3 right-3 top-4">
          <ProfileHeader
            imageUrl={account?.imageUrl}
            displayName={account?.displayName || 'Your Name'}
          />
        </div>

        {/* Right-side engagement buttons */}
        <div className="absolute bottom-20 right-3 flex flex-col items-center gap-5">
          {/* Like button */}
          <button className="flex flex-col items-center gap-1">
            <div className="backdrop-blur-xs flex h-12 w-12 items-center justify-center rounded-full bg-black/20">
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
              </svg>
            </div>
            <span className="text-xs font-medium text-white">1.2K</span>
          </button>

          {/* Comment button */}
          <button className="flex flex-col items-center gap-1">
            <div className="backdrop-blur-xs flex h-12 w-12 items-center justify-center rounded-full bg-black/20">
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
              </svg>
            </div>
            <span className="text-xs font-medium text-white">234</span>
          </button>

          {/* Share button */}
          <button className="flex flex-col items-center gap-1">
            <div className="backdrop-blur-xs flex h-12 w-12 items-center justify-center rounded-full bg-black/20">
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
              </svg>
            </div>
            <span className="text-xs font-medium text-white">89</span>
          </button>
        </div>

        {/* Caption overlay (bottom left) */}
        <div className="absolute bottom-3 left-3 right-16">
          {postContent?.caption && (
            <div className="backdrop-blur-xs rounded bg-black/30 p-2">
              <p className="line-clamp-3 text-sm text-white">{postContent.caption}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const ProfileHeader = ({
  imageUrl,
  displayName,
}: {
  imageUrl: string | undefined
  displayName: string
}) => {
  return (
    <div className="flex items-center gap-2">
      {/* Profile picture */}
      <div className="h-10 w-10 overflow-hidden rounded-full border-2 border-white">
        {imageUrl ? (
          <img src={imageUrl} alt="Profile" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-700">
            <span className="text-sm text-white">
              {displayName?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
        )}
      </div>

      {/* Username */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-white">
          {displayName || 'Not authenticated'}
        </span>
        <button className="rounded bg-[#FE2C55] px-4 py-1 text-xs font-semibold text-white">
          Follow
        </button>
      </div>
    </div>
  )
}
